// What the "Мій сайт" pages derive from the API's ProviderSite (apps/api provider-sites module),
// the same rules the mobile app follows (apps/app/lib/providerSite.ts).

export type ProviderSiteStatus = "DRAFT" | "PUBLISHED" | "DISABLED";
export type ProviderSiteJobStatus = "QUEUED" | "RUNNING" | "DONE" | "FAILED";
// The languages a site renders in, as the API takes them (SITE_LANGS in apps/api site-langs.ts).
export const SITE_LANGUAGES = ["uk", "en", "ru"] as const;
export type SiteLanguage = (typeof SITE_LANGUAGES)[number];
// OWN_ASSISTANT: a line saying the Provider's own ChatGPT or Claude changed the draft.
export type ProviderSiteMessageRole = "PROVIDER" | "AGENT" | "OWN_ASSISTANT";

export interface ProviderSiteJobLike {
  status: ProviderSiteJobStatus;
  step?: string | null;
}

export interface ProviderSiteLike {
  status: ProviderSiteStatus;
  hasDraft: boolean;
  publishedVersion?: number | null;
  activeJob?: ProviderSiteJobLike | null;
  remainingBuilds?: number | null;
  remainingEdits?: number | null;
}

// The site's state as one word: the app shows it next to "Мій сайт" in the Profile menu, the admin
// on the page header.
export type SiteStatusKey = "create" | "draft" | "published" | "disabled";

export function getSiteStatusKey(
  site: Pick<ProviderSiteLike, "status"> | null | undefined
): SiteStatusKey {
  if (!site) return "create";
  if (site.status === "PUBLISHED") return "published";
  if (site.status === "DISABLED") return "disabled";
  return "draft";
}

export const MAX_SITE_MESSAGE_LENGTH = 2000;
// How often the pages re-read the site while the agent works on a message.
export const ACTIVE_JOB_POLL_MS = 4000;

export function isJobActive(job: ProviderSiteJobLike | null | undefined) {
  return job?.status === "QUEUED" || job?.status === "RUNNING";
}

// "Скасувати зміни" on the draft card: only a published site has something to go back to.
export function canDiscardDraft(
  site: Pick<ProviderSiteLike, "hasDraft" | "publishedVersion"> | null | undefined
) {
  return Boolean(site?.hasDraft && site.publishedVersion);
}

// The draft card shows while the draft differs from the site, or before the first publish.
export function shouldShowDraft(
  site: { hasDraft: boolean; hasUnpublishedChanges: boolean; status: ProviderSiteStatus } | null
) {
  return Boolean(site?.hasDraft && (site.hasUnpublishedChanges || site.status === "DRAFT"));
}

// The agent's steps as the chat shows them; `queued` counts as the first step under way.
export const SITE_JOB_STEPS = ["reading", "designing", "checking"] as const;
export type SiteJobStep = (typeof SITE_JOB_STEPS)[number];
export type SiteJobStepState = "done" | "current" | "next";

export function getJobStepStates(
  step: string | null | undefined
): { step: SiteJobStep; state: SiteJobStepState }[] {
  const index = Math.max(0, SITE_JOB_STEPS.indexOf(step as SiteJobStep));
  return SITE_JOB_STEPS.map((name, position) => ({
    step: name,
    state: position < index ? "done" : position === index ? "current" : "next",
  }));
}

// The next message builds the site when there is nothing yet, else it edits it; each has its
// own daily limit.
export function getNextMessageKind(site: ProviderSiteLike | null | undefined): "BUILD" | "EDIT" {
  return site && (site.hasDraft || site.publishedVersion) ? "EDIT" : "BUILD";
}

// null: unlimited, or not known yet (no site: the API tells after the first message).
export function getRemainingForNextMessage(
  site: ProviderSiteLike | null | undefined
): number | null {
  if (!site) return null;
  return (
    (getNextMessageKind(site) === "BUILD" ? site.remainingBuilds : site.remainingEdits) ?? null
  );
}

export function canSendSiteMessage(site: ProviderSiteLike | null | undefined, text: string) {
  const message = text.trim();
  if (!message || message.length > MAX_SITE_MESSAGE_LENGTH) return false;
  if (isJobActive(site?.activeJob)) return false;
  const remaining = getRemainingForNextMessage(site);
  return remaining === null || remaining > 0;
}

// The describe page shows a few of these ideas, a different few each time, so Providers do not
// all start from the same words (keys under sitePage.empty.examples).
export const SITE_EXAMPLE_COUNT = 12;

export function pickSiteExampleKeys(count = 3, random: () => number = Math.random): string[] {
  const keys = Array.from({ length: SITE_EXAMPLE_COUNT }, (_, index) => `example${index + 1}`);
  for (let index = keys.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    [keys[index], keys[swap]] = [keys[swap], keys[index]];
  }
  return keys.slice(0, count);
}

// The site's address as people type it: no scheme, no trailing slash.
export function getSiteHost(url: string) {
  return url.replace(/^https:\/\//, "").replace(/\/$/, "");
}
